import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Feather, KeyRound, ShieldCheck } from "lucide-react";

export default function Setup() {
  const { setup, setupComplete, loading } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [summaryTime, setSummaryTime] = useState("07:00");
  const [totp, setTotp] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && setupComplete) nav("/login");
  }, [loading, setupComplete, nav]);

  const submitAccount = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (passphrase.length < 6) return toast.error("Tier A passphrase must be at least 6 characters.");
    setBusy(true);
    try {
      const data = await setup({ email, password, tier_a_passphrase: passphrase, summary_time: summaryTime });
      setTotp({ secret: data.totp_secret, uri: data.totp_uri });
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="setup-page">
      <div className="w-full max-w-lg space-y-8 fade-up">
        <div>
          <div className="flex items-center gap-2 overline">
            <Feather size={14}/> Initial setup
          </div>
          <h2 className="font-display text-4xl mt-2">A sanctuary built quietly.</h2>
          <p className="text-sm mt-2" style={{ color: "var(--elle-ink-dim)" }}>
            Three gates: password for the door, passphrase for the ledger, 2FA for the safe.
          </p>
        </div>

        {step === 1 && (
          <form onSubmit={submitAccount} className="elle-surface p-8 space-y-5 rounded-sm">
            <div>
              <label className="overline block mb-2">Email</label>
              <Input data-testid="setup-email" type="email" required
                     value={email} onChange={(e)=>setEmail(e.target.value)}/>
            </div>
            <div>
              <label className="overline block mb-2">Password (dashboard login)</label>
              <Input data-testid="setup-password" type="password" required minLength={8}
                     value={password} onChange={(e)=>setPassword(e.target.value)}/>
              <p className="text-xs mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                At least 8 characters. Never shared.
              </p>
            </div>
            <div>
              <label className="overline block mb-2" style={{ color: "var(--elle-amber)" }}>
                Tier A passphrase (rule changes)
              </label>
              <Input data-testid="setup-passphrase" type="password" required minLength={6}
                     value={passphrase} onChange={(e)=>setPassphrase(e.target.value)}/>
              <p className="text-xs mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                Used to approve behaviour, voice, and posting-rule changes.
              </p>
            </div>
            <div>
              <label className="overline block mb-2">Daily summary delivery time</label>
              <Input data-testid="setup-summary-time" type="time"
                     value={summaryTime} onChange={(e)=>setSummaryTime(e.target.value)}/>
            </div>
            <Button data-testid="setup-submit" type="submit" disabled={busy}
                    className="btn-amber rounded-sm w-full h-11">
              {busy ? "Preparing the study…" : "Continue"}
            </Button>
          </form>
        )}

        {step === 2 && totp && (
          <div className="elle-surface p-8 space-y-5 rounded-sm"
               style={{ borderColor: "var(--elle-oxblood)" }}>
            <div className="flex items-center gap-2 overline" style={{ color: "var(--elle-oxblood-bright)" }}>
              <ShieldCheck size={14}/> Tier B — 2FA for hard-limit changes
            </div>
            <p className="text-sm" style={{ color: "var(--elle-ink-dim)" }}>
              Open your authenticator app (Google Authenticator, Authy, 1Password) and add this secret.
              This code is required whenever a hard-limit change is proposed.
            </p>
            <div className="elle-surface-2 p-5 rounded-sm space-y-3">
              <div>
                <div className="overline mb-1">Manual entry secret</div>
                <div className="font-mono-elle text-lg tracking-widest break-all"
                     data-testid="totp-secret" style={{ color: "var(--elle-amber)" }}>
                  {totp.secret}
                </div>
              </div>
              <div>
                <div className="overline mb-1">Or scan/copy URI</div>
                <div className="font-mono-elle text-xs break-all"
                     style={{ color: "var(--elle-ink-dim)" }}>
                  {totp.uri}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2 text-xs"
                 style={{ color: "var(--elle-ink-faint)" }}>
              <KeyRound size={14}/>
              <span>
                Save the secret now. You will need it before any hard-limit change is accepted.
              </span>
            </div>
            <Button data-testid="setup-continue-btn"
                    onClick={()=>nav("/")} className="btn-amber rounded-sm w-full h-11">
              Enter the study
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
